import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  Grid,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  useTheme,
  alpha,
  TablePagination,
} from '@mui/material';
import {
  Search,
  Refresh,
  Email,
  Sms,
  WhatsApp,
  NotificationsActive,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Visibility,
  Message,
  EventNote,
  Send,
  Replay,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import mensagemService, { Mensagem } from '../../services/mensagemService';
import { getCountryByNumber } from '../../utils/countries';
import WhatsappViewMsg from '../modals/WhatsappViewMsg';
import EmailViewMsg from '../modals/EmailViewMsg';
import SmsViewMsg from '../modals/SmsViewMsg';
import Swal from 'sweetalert2';

const obterDadosPais = (numero: string, ddi?: string | null) => {
  if (ddi) return getCountryByNumber(ddi);
  let limpo = numero.replace(/\D/g, '');
  if (limpo.startsWith('55') && (limpo.length === 12 || limpo.length === 13)) {
    return getCountryByNumber('55');
  }
  if (limpo.length === 10 || limpo.length === 11) {
    return getCountryByNumber('55');
  }
  return getCountryByNumber(limpo);
};

const formatarNumeroComDddParenteses = (numero: string, ddi?: string | null): string => {
  if (!numero) return '';
  let limpo = numero.replace(/\D/g, '');
  
  let ddiEncontrado = ddi || '';
  if (!ddiEncontrado && limpo.startsWith('55') && (limpo.length === 12 || limpo.length === 13)) {
    ddiEncontrado = '55';
  }
  
  if (ddiEncontrado && limpo.startsWith(ddiEncontrado)) {
    limpo = limpo.slice(ddiEncontrado.length);
  }
  
  if (limpo.length === 10) {
    return `${ddiEncontrado ? '+' + ddiEncontrado + ' ' : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 6)}-${limpo.slice(6)}`;
  } else if (limpo.length === 11) {
    return `${ddiEncontrado ? '+' + ddiEncontrado + ' ' : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 7)}-${limpo.slice(7)}`;
  }
  
  return numero;
};

interface ClientMessagesProps {
  clientId: number;
}

const ClientMessages: React.FC<ClientMessagesProps> = ({ clientId }) => {
  const theme = useTheme();
  const primario = theme.palette.primary.main;

  const [messages, setMessages] = useState<Mensagem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalMessages, setTotalMessages] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMensagem, setSelectedMensagem] = useState<Mensagem | null>(null);
  const [whatsappViewOpen, setWhatsappViewOpen] = useState(false);
  const [emailViewOpen, setEmailViewOpen] = useState(false);
  const [smsViewOpen, setSmsViewOpen] = useState(false);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await mensagemService.getMensagens({
        cliente_id: clientId,
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter as any || undefined,
        tipo: typeFilter as any || undefined,
        search: searchTerm || undefined,
        sortBy: 'createdAt',
        sortOrder: 'DESC'
      });

      if (response.success && response.data) {
        setMessages(response.data);
        if (response.pagination) {
          setTotalMessages(response.pagination.total);
        }
      } else {
        setError(response.message || 'Erro ao carregar mensagens');
      }
    } catch (err) {
      setError('Erro ao carregar histórico de mensagens');
      console.error('Erro ao buscar mensagens:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchMessages();
    }
  }, [clientId, page, rowsPerPage, statusFilter, typeFilter]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (page === 0) {
        fetchMessages();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const handlePageChange = (event: React.ChangeEvent<unknown> | null, value: number) => {
    setPage(value);
  };

  const handleRefresh = () => {
    fetchMessages();
  };

  const handleSendNow = async (id: number) => {
    try {
      const result = await Swal.fire({
        title: 'Enviar mensagem agora?',
        text: 'A mensagem será enviada imediatamente',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, enviar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: theme.palette.primary.main,
      });

      if (result.isConfirmed) {
        const response = await mensagemService.sendNowMensagem(id);

        if (response.outcome === 'cancelado') {
          Swal.fire({
            icon: 'info',
            title: 'Mensagem cancelada',
            text: response.message || 'A mensagem foi cancelada pois a fatura vinculada não está mais pendente.',
          });
          fetchMessages();
        } else if (response.outcome === 'reagendado') {
          Swal.fire({
            icon: 'info',
            title: 'Mensagem reagendada',
            text: response.message || 'A mensagem foi reagendada pois o contrato está em observação.',
          });
          fetchMessages();
        } else if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Sucesso!',
            text: 'Mensagem enviada com sucesso',
            timer: 2000,
          });
          fetchMessages();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erro!',
            text: response.message || 'Erro ao enviar mensagem',
          });
        }
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: error?.response?.data?.message || error?.message || 'Erro ao enviar mensagem',
      });
    }
  };

  const handleResend = async (id: number) => {
    try {
      const result = await Swal.fire({
        title: 'Reenviar mensagem?',
        text: 'A mensagem será marcada para reenvio',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, reenviar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: theme.palette.primary.main,
      });

      if (result.isConfirmed) {
        const response = await mensagemService.resendMensagem(id);

        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Sucesso!',
            text: 'Mensagem marcada para reenvio',
            timer: 2000,
          });
          fetchMessages();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erro!',
            text: response.message || 'Erro ao reenviar mensagem',
          });
        }
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: error?.message || 'Erro ao reenviar mensagem',
      });
    }
  };

  const handleViewDetails = async (id: number) => {
    try {
      const response = await mensagemService.getMensagemById(id);
      if (response.success && response.data) {
        setSelectedMensagem(response.data);
        switch (response.data.tipo) {
          case 'whatsapp':
            setWhatsappViewOpen(true);
            break;
          case 'email':
            setEmailViewOpen(true);
            break;
          case 'sms':
            setSmsViewOpen(true);
            break;
          default:
            Swal.fire({
              title: 'Detalhes da Mensagem',
              html: `<div style="text-align: left;"><pre style="white-space: pre-wrap; font-family: inherit;">${response.data.conteudo}</pre></div>`,
              confirmButtonText: 'Fechar'
            });
            break;
        }
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Erro!', text: 'Erro ao carregar detalhes da mensagem' });
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'enviado':
        return <CheckCircle fontSize="small" />;
      case 'aguardando_envio':
        return <Schedule fontSize="small" />;
      case 'enfileirado':
        return <Schedule fontSize="small" />;
      case 'falhou':
        return <ErrorIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enviado':
        return 'success';
      case 'aguardando_envio':
        return 'warning';
      case 'enfileirado':
        return 'info';
      case 'falhou':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatStatus = (status: string) => {
    const statusMap: Record<string, string> = {
      enviado: 'Enviado',
      aguardando_envio: 'Aguardando Envio',
      enfileirado: 'Na fila de envio',
      falhou: 'Falhou',
    };
    return statusMap[status] || status;
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'email':
        return <Email fontSize="small" />;
      case 'sms':
        return <Sms fontSize="small" />;
      case 'whatsapp':
        return <WhatsApp fontSize="small" />;
      case 'aplicativo':
        return <NotificationsActive fontSize="small" />;
      default:
        return null;
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'email':
        return 'primary';
      case 'sms':
        return 'secondary';
      case 'whatsapp':
        return 'success';
      case 'aplicativo':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatTipo = (tipo: string) => {
    const tipoMap: Record<string, string> = {
      email: 'E-mail',
      sms: 'SMS',
      whatsapp: 'WhatsApp',
      aplicativo: 'Aplicativo',
    };
    return tipoMap[tipo] || tipo;
  };

  const renderDestino = (msg: Mensagem) => {
    let isPhone = false;
    let numero = '';
    let ddi = '';
    let text = '-';
    
    if (msg.contato) {
      if (msg.contato.tipo === 'celular' || msg.contato.tipo === 'fixo') {
        isPhone = true;
        numero = msg.contato.numero || '';
        ddi = msg.contato.ddi || '';
        text = formatarNumeroComDddParenteses(numero, ddi);
      } else if (msg.contato.tipo === 'email') {
        text = msg.contato.email || '-';
      }
    } else if (msg.destino) {
      text = msg.destino;
      if (msg.tipo === 'sms' || msg.tipo === 'whatsapp') {
        isPhone = true;
        numero = msg.destino;
        text = formatarNumeroComDddParenteses(numero, null);
      }
    }

    if (isPhone && numero) {
      const country = obterDadosPais(numero, ddi);
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          {country && (
            <Tooltip title={country.label}>
              <img
                loading="lazy"
                width="16"
                src={new URL(`../../assets/countries/w20/${country.code.toLowerCase()}.png`, import.meta.url).href}
                alt={country.label}
                style={{ borderRadius: 2 }}
              />
            </Tooltip>
          )}
          <Typography variant="body2" sx={{ fontSize: '0.75rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {text}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{ fontSize: '0.75rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {text}
      </Typography>
    );
  };

  if (loading && page === 0 && messages.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Carregando histórico de mensagens...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header e Filtros */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
          Histórico de Mensagens ({totalMessages})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Atualizar">
            <IconButton onClick={handleRefresh} size="small">
              <Refresh sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={12} sm={4} md={4}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar no conteúdo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: '1rem' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">Todos os status</MenuItem>
              <MenuItem value="enviado">Enviado</MenuItem>
              <MenuItem value="aguardando_envio">Aguardando Envio</MenuItem>
              <MenuItem value="enfileirado">Na fila de envio</MenuItem>
              <MenuItem value="falhou">Falhou</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              label="Tipo"
            >
              <MenuItem value="">Todos os tipos</MenuItem>
              <MenuItem value="whatsapp">WhatsApp</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="email">E-mail</MenuItem>
              <MenuItem value="aplicativo">PWA/App</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader sx={{ '& .MuiTableBody-root .MuiTableCell-root': { py: 0.5, fontSize: '0.78rem' } }}>
            <TableHead>
              <TableRow sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                },
              }}>
                <TableCell>Status</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Evento</TableCell>
                <TableCell>Destino</TableCell>
                <TableCell>Data Criação</TableCell>
                <TableCell>Data Agendada</TableCell>
                <TableCell>Data Envio</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Message sx={{ fontSize: 40, color: 'text.disabled', mb: 1, opacity: 0.5 }} />
                    <Typography color="text.secondary">Nenhuma mensagem encontrada</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((msg) => (
                  <TableRow key={msg.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>

                    <TableCell>
                      <Chip
                        icon={getStatusIcon(msg.status) || undefined}
                        label={formatStatus(msg.status)}
                        color={getStatusColor(msg.status) as any}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={getTipoIcon(msg.tipo) || undefined}
                        label={formatTipo(msg.tipo)}
                        color={getTipoColor(msg.tipo) as any}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <EventNote sx={{ fontSize: '0.8rem', color: 'info.main' }} />
                        <Typography variant="body1" sx={{ fontSize: '0.75rem' }}>
                          {msg.evento?.replace(/_/g, ' ') || 'Manual'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {renderDestino(msg)}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {formatDate(msg.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {formatDate(msg.agendado_para)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {formatDate(msg.data_envio)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                        <Tooltip title="Ver detalhes">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(msg.id)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {msg.status === 'aguardando_envio' && (
                          <Tooltip title="Enviar agora">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleSendNow(msg.id)}
                            >
                              <Send fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {msg.status === 'falhou' && (
                          <Tooltip title="Reenviar">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleResend(msg.id)}
                            >
                              <Replay fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalMessages > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 0.5,
              px: 2,
              bgcolor: alpha(primario, 0.04),
              borderTop: `2px solid ${alpha(primario, 0.2)}`,
            }}
          >
            <TablePagination
              component="div"
              count={totalMessages}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Linhas por página"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                '.MuiTablePagination-toolbar': {
                  minHeight: 34,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 2,
                  '& .MuiTablePagination-spacer': { display: 'none' },
                },
                '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.875rem' },
                '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.875rem' },
                '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.875rem' },
                '.MuiTablePagination-actions': { ml: 0.5 },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Modais de Visualização */}
      <WhatsappViewMsg
        open={whatsappViewOpen}
        onClose={() => setWhatsappViewOpen(false)}
        mensagem={selectedMensagem}
      />
      <EmailViewMsg
        open={emailViewOpen}
        onClose={() => setEmailViewOpen(false)}
        mensagem={selectedMensagem}
      />
      <SmsViewMsg
        open={smsViewOpen}
        onClose={() => setSmsViewOpen(false)}
        mensagem={selectedMensagem}
      />
    </Box>
  );
};

export default ClientMessages;
